import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Setup axios response interceptor for Twitter verification errors
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Handle Twitter verification requirement
        if (error.response?.data?.requiresTwitterVerification) {
          // Dispatch custom event that can be caught by components
          window.dispatchEvent(new CustomEvent('twitterVerificationRequired', {
            detail: {
              message: error.response.data.message || 'Twitter verification required'
            }
          }));
        }
        return Promise.reject(error);
      }
    );

    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get('/api/auth/me');
      setUser(response.data.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post('/api/auth/login', { email, password });
    const { token, ...userData } = response.data.data;
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    return response.data;
  };

  const loginWithToken = async (token) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    await fetchUser();
  };

  const register = async (username, email, password, accountType) => {
    const response = await axios.post('/api/auth/register', {
      username,
      email,
      password,
      accountType
    });
    const { token, ...userData } = response.data.data;
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    return response.data;
  };

  const logout = async () => {
    try {
      console.log('Logging out...');
      await axios.post('/api/auth/logout');
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      console.log('Logged out successfully');
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    login,
    loginWithToken,
    register,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.accountType === 'admin',
    isIndieDev: user?.accountType === 'indie_dev' || user?.accountType === 'admin'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
